<script>
  import { useRouter } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../../stores/authStore.svelte.js';
  import PageHeader from '../../components/PageHeader.svelte';
  import DataTable from '../../components/DataTable.svelte';
  import Pagination from '../../components/Pagination.svelte';

  const router = useRouter();

  let loading = $state(true);
  let error = $state(null);
  let items = $state([]);
  let pagination = $state(null);
  let page = $state(1);

  // Filters. `search` is what the input shows; `query` is the debounced value
  // actually sent, so typing does not fire a request per keystroke.
  let search = $state('');
  let query = $state('');
  let status = $state('all');
  let debounce;

  function onSearch(event) {
    search = event.currentTarget.value;
    clearTimeout(debounce);
    debounce = setTimeout(() => { query = search.trim(); page = 1; }, 300);
  }

  let showCreate = $state(false);
  let form = $state({ name: '', email: '', phone: '', address: '' });
  let creating = $state(false);

  async function load() {
    loading = true;
    error = null;
    try {
      const result = await api.listTenants({ page, limit: 25, q: query || undefined, status });
      items = result.items;
      pagination = result.pagination;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { page; query; status; load(); });

  async function submitCreate(event) {
    event.preventDefault();
    creating = true;
    try {
      const tenant = await api.createTenant({
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
      });
      toast.success(`Created ${tenant.name}. Work through its readiness checklist next.`);
      form = { name: '', email: '', phone: '', address: '' };
      showCreate = false;
      // The tenant switcher is fed from /me/context; refresh it so the new
      // tenant is selectable without a reload.
      await authStore.loadContext(authStore.tenantId);
      router.push({ name: 'admin-tenant-detail', params: { id: tenant.id } });
    } catch (e) {
      toast.error(e.message);
    } finally {
      creating = false;
    }
  }

  const onboardingLabels = {
    not_started: 'Not started',
    importing: 'Importing',
    training: 'Training',
    ready: 'Ready',
    operational: 'Operational',
    paused: 'Paused',
  };
</script>

{#snippet nameCell(row)}
  <span class:opacity-60={!row.is_active}>{row.name}</span>
  {#if !row.is_active}
    <span class="p-badge ml-2" style="background-color: var(--p-danger-bg); color: var(--p-danger)">
      Inactive
    </span>
  {/if}
  {#if row.email}<p class="text-xs p-faint">{row.email}</p>{/if}
{/snippet}

{#snippet setupCell(row)}
  <span class="text-xs">
    <span title="Hostaway account connected">
      <i class="fas {row.hostaway_configured ? 'fa-circle-check' : 'fa-circle-xmark'}"
         style="color: var({row.hostaway_configured ? '--p-success' : '--p-text-faint'})"
         aria-hidden="true"></i> Hostaway
    </span>
    <span class="ml-3" title="Auto-responder enabled">
      <i class="fas {row.autoresponder_enabled ? 'fa-circle-check' : 'fa-circle-xmark'}"
         style="color: var({row.autoresponder_enabled ? '--p-success' : '--p-text-faint'})"
         aria-hidden="true"></i> Auto-responder
    </span>
  </span>
{/snippet}

<PageHeader title="Tenants" subtitle="Property-management companies using STRAO.">
  {#snippet children()}
    <button type="button" class="p-btn p-btn-primary" onclick={() => (showCreate = !showCreate)}>
      <i class="fas fa-plus" aria-hidden="true"></i> New tenant
    </button>
  {/snippet}
</PageHeader>

{#if showCreate}
  <form class="p-card mb-6 space-y-4 p-4" onsubmit={submitCreate}>
    <div class="grid gap-4 sm:grid-cols-2">
      <div>
        <label for="t-name" class="text-xs font-medium p-muted">Name (required)</label>
        <input id="t-name" class="p-input mt-1" bind:value={form.name} required maxlength="255" />
      </div>
      <div>
        <label for="t-email" class="text-xs font-medium p-muted">Contact email</label>
        <input id="t-email" class="p-input mt-1" type="email" bind:value={form.email} maxlength="255" />
      </div>
      <div>
        <label for="t-phone" class="text-xs font-medium p-muted">Phone</label>
        <input id="t-phone" class="p-input mt-1" bind:value={form.phone} maxlength="50" />
      </div>
      <div>
        <label for="t-address" class="text-xs font-medium p-muted">Address</label>
        <input id="t-address" class="p-input mt-1" bind:value={form.address} />
      </div>
    </div>
    <p class="rounded-lg px-3 py-2 text-xs" style="background-color: var(--p-info-bg)">
      <i class="fas fa-circle-info" aria-hidden="true"></i>
      A new tenant starts with its auto-responder switched off, so nothing is drafted or sent
      until you connect Hostaway and turn it on. The tenant page's readiness checklist walks
      through the rest.
    </p>
    <div class="flex justify-end gap-2">
      <button type="button" class="p-btn p-btn-quiet" onclick={() => (showCreate = false)}>Cancel</button>
      <button type="submit" class="p-btn p-btn-primary" disabled={creating || !form.name.trim()}>
        Create tenant
      </button>
    </div>
  </form>
{/if}

<div class="mb-4 flex flex-wrap items-end gap-3">
  <div class="min-w-[16rem] flex-1">
    <label for="t-search" class="text-xs font-medium p-muted">Search</label>
    <input id="t-search" class="p-input mt-1" type="search" placeholder="Name or email"
           value={search} oninput={onSearch} />
  </div>
  <div>
    <label for="t-status" class="text-xs font-medium p-muted">Status</label>
    <select id="t-status" class="p-input mt-1" bind:value={status} onchange={() => (page = 1)}>
      <option value="all">All</option>
      <option value="active">Active</option>
      <option value="inactive">Inactive</option>
    </select>
  </div>
</div>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else}
  <DataTable
    columns={[
      { key: 'name', label: 'Tenant', snippet: nameCell },
      { key: 'setup', label: 'Setup', snippet: setupCell },
      { key: 'onboarding_status', label: 'Lifecycle',
        render: (r) => onboardingLabels[r.onboarding_status] ?? (r.onboarding_status || 'No config') },
      { key: 'property_count', label: 'Properties', align: 'right', render: (r) => String(r.property_count) },
      { key: 'member_count', label: 'Users', align: 'right', render: (r) => String(r.member_count) },
      { key: 'created_at', label: 'Created',
        render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—') },
    ]}
    rows={items} {loading}
    emptyMessage={query || status !== 'all' ? 'No tenants match these filters.' : 'No tenants yet.'}
    onRowClick={(row) => router.push({ name: 'admin-tenant-detail', params: { id: row.id } })} />
  <Pagination {pagination} onPage={(p) => (page = p)} />
{/if}
